import { BaseApi } from "../shared/api";
import * as types from "./actionTypes";
import { startLoading, stopLoading } from "../shared/actions";
import * as sharedTypes from "../shared/actionTypes";
import * as url from "./apiUrls";
import moment from 'moment';
import creditLineValidator, {
  isExpired,
  showExpiringRclBannerNotification,
} from '../../utils/creditLineValidator';
import creditLimitValidator from '../../utils/creditLimitValidator';

function receiveCreditLimit(payload) {
  return { type: types.CREDIT_LIMIT_FETCH_SUCCESS, payload: payload }
}

function receiveCreditLine(payload) {
  return { type: types.CREDIT_LINE_FETCH_SUCCESS, payload: payload }
}

function receiveFcLead(payload) {
  return { type: types.FC_LEAD_FETCH_SUCCESS, payload: payload }
}

function receiveFcPayment(payload) {
  return { type: types.FC_PAYMENT_FETCH_SUCCESS, payload: payload }
}

function showErrorOnCreditLineResponse() {
  return {
    type: 'NOTIFY',
    payload: {
      header: 'There seems to be a problem.',
      body:
        'Please try refreshing this page, or contact Support if the problem persists.',
      className: '-warning',
      dismissable: false,
      duration: -1,
    },
  };
}

function checkRclExpiry(end_timestamp, upcoming_rcl, dispatch) {
  const days_remaining = moment(end_timestamp).diff(moment(), 'days');
  if (!showExpiringRclBannerNotification(days_remaining, upcoming_rcl)) return;

  const message = `Your revolving credit line is expiring in ${days_remaining} day(s). Submit additional documents to reapply.`;
  return dispatch({ type: types.SET_RCL_NOTIFICATION, payload: message });
}

export function fetchCreditLimit(onSuccess = () => { }) {
  return (dispatch, getState) => {
    dispatch(startLoading('creditLimit'));
    const { user, company } = getState();
    if (!user || !company) return;
    let api;

    api = new BaseApi(dispatch, getState, {
      headers: { "x-api-key": process.env.REACT_APP_LMS_API_KEY }
    });
    const apiUrl = url.creditLimitLMS(company.id);

    return api.getRequest(
      apiUrl,
      response => {
        const data = response.data.data.attributes;
        const {
          active_rcl_offers_amount_total,
          amount_balance_outstanding,
          amount_to_paydown,
          amount_to_paydown_with_interest,
          credit_remaining,
          credit_used,
          expected_unlock_date,
          is_credit_limit_locked,
          max_additional_credit_limit,
          max_potential_credit_limit,
          overdue_count,
          target_paydown_percent,
        } = data
        const validCreditLimit = creditLimitValidator({
          active_rcl_offers_amount_total,
          amount_balance_outstanding,
          amount_to_paydown,
          amount_to_paydown_with_interest,
          credit_remaining,
          credit_used,
          expected_unlock_date,
          is_credit_limit_locked,
          max_additional_credit_limit,
          max_potential_credit_limit,
          overdue_count,
          target_paydown_percent,
        });
        onSuccess(response, validCreditLimit);
        dispatch(receiveCreditLimit(data));
        dispatch(stopLoading('creditLimit'));
      },
      errors => {
        dispatch({
          type: sharedTypes.HANDLE_ERRORS,
          payload: {
            data: {},
            header: "Oops, something went wrong",
            body: "We are unable to show you your credit limit and upcoming payments (if any). Please contact Support for help.",
            duration: -1,
          }
        });
      }
    )
  }
}

export function fetchCreditLine(onSuccess = () => { }) {
  return (dispatch, getState) => {
    dispatch(startLoading('creditLine'));
    const { user, company } = getState();
    if (!user || !company) return;
    let api;

    api = new BaseApi(dispatch, getState)
    const apiUrl = url.creditLineUrl(company.id);

    return api.getRequest(
      apiUrl,
      response => {
        if (response.status === 204) {
          onSuccess(response, false);
          dispatch(stopLoading('creditLine'));
        } else {
          const data = response.data.data.attributes
          const {
            option_terms_rate,
            convenience_fee_type,
            end_timestamp,
            start_timestamp,
            pre_cutoff_live_date,
            post_cutoff_live_date,
            upcoming_rcl_start_timestamp,
          } = data;
          const validCreditLine = creditLineValidator({
            option_terms_rate,
            convenience_fee_type,
            end_timestamp,
            start_timestamp,
            pre_cutoff_live_date,
            post_cutoff_live_date,
            upcoming_rcl_start_timestamp,
          });
          dispatch(receiveCreditLine(data));          
          onSuccess(response, validCreditLine);
          if (!validCreditLine) {
            if (!isExpired(end_timestamp)) {
              dispatch(showErrorOnCreditLineResponse());
            }
          } else {
            checkRclExpiry(
              end_timestamp,
              upcoming_rcl_start_timestamp,
              dispatch
            );
          }
          dispatch(stopLoading('creditLine'));
        }
      },
      errors => {
        dispatch({ type: types.CREDIT_LINE_FETCH_ERROR });
        dispatch({
          type: sharedTypes.HANDLE_ERRORS,
          payload: {
            data: {},
            header: "Oops, something went wrong",
            body: "We are unable to show you your credit line.",
            duration: -1,
          }
        });
      }
    )
  }
}

// FirstCircle Connect (FC) related endpoints
export function fetchFcLead() {
  return (dispatch, getState) => {
    dispatch(startLoading('fcLead'));
    const { user } = getState();
    if (!user || !user.active_business_trustpass) { return }
    const leadId = user.active_business_trustpass.lead_id;
    if (!leadId) { return }

    const fcHubUrl = process.env.REACT_APP_FCHUB_URL;
    const apiUrl = `${fcHubUrl}/leads/${leadId}.json`

    return fetch(apiUrl, {
      method: "GET",
      headers: new Headers({
        "Accept": "application/json",
      }),
      credentials: 'include'
    })
      .then(response => response.json())
      .then(json => {
        dispatch(receiveFcLead(json));
        dispatch(stopLoading('fcLead'));
      })
      .catch(error => {
        console.error(error);
      });
  }
}

export function fetchFcPayment(fcPaymentId, onSuccess = () => { }) {
  return (dispatch, getState) => {
    dispatch(startLoading('payment'));
    const { user } = getState();
    if (!user || !user.active_business_trustpass) return;

    let api = new BaseApi(dispatch, getState, {});

    const fcHubUrl = process.env.REACT_APP_FCHUB_URL;
    const leadId = user.active_business_trustpass.lead_id
    const apiUrl = `${fcHubUrl}/leads/${leadId}/fc_payments/${fcPaymentId}.json`

    return api.getRequest(
      apiUrl,
      response => {
        const data = response.data;
        dispatch(receiveFcPayment(data));
        dispatch(stopLoading('payment'));
      },
      _errors => {
        onSuccess({}, false)
      },
      true,
    )
  }
}



// WEBPACK FOOTER //
// ./src/features/CreditLimit/actions.js